import { useEffect, useState } from 'react';

import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import { Collapse, useTheme } from '@mui/material';
import { Forward, Reply } from 'lucide-react';

import type { EmailActivityItemContentFragment } from '@graphql/__generated__/graphql';
import { useActivityContext } from '@modules/_shared/components/feed/ActivityProvider';

type EmailExtraTimelineItemProps = {
  isLast: boolean;
  mode: 'reply' | 'forward' | null;
  activity: EmailActivityItemContentFragment;
  onClose: () => void;
};

export const EmailExtraTimelineItem = ({
  isLast,
  mode,
  activity,
  onClose,
}: EmailExtraTimelineItemProps) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const { EmailFormComponent } = useActivityContext();
  const [formMode, setFormMode] = useState<'reply' | 'forward' | null>(mode);
  const Icon = formMode === 'reply' ? Reply : Forward;

  useEffect(() => {
    // Allows the form to be closed and keep the form mode in the state
    if (mode != null) {
      setFormMode(mode);
    }
  }, [mode]);

  if (activity.email_message == null || !EmailFormComponent) {
    return null;
  }

  return (
    <Collapse in={mode != null}>
      <TimelineItem sx={{ minHeight: 0 }}>
        <TimelineSeparator sx={{ flex: 0 }}>
          <TimelineConnector
            sx={{ bgcolor: 'grey.200', height: 14, flexGrow: 0 }}
          />
          <TimelineDot
            sx={{
              bgcolor: 'transparent',
              boxShadow: 'none',
              p: 0,
              borderTopWidth: 6,
              borderBottomWidth: 6,
              my: 0,
            }}
          >
            <Icon size={18} color={palette.primary.main} strokeWidth={2.5} />
          </TimelineDot>
          {!isLast && <TimelineConnector sx={{ bgcolor: 'grey.200' }} />}
        </TimelineSeparator>

        <TimelineContent
          sx={{
            py: 0.5,
            pl: { xs: 1, sm: 2 },
            pr: 0,
            minHeight: 0,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {formMode === 'reply' ? (
            <EmailFormComponent
              mode="reply"
              replyToMessage={{
                messageId: activity.email_message.message_id,
                createdBy: activity.email_message.created_by,
                subject: activity.email_message.subject,
                from: activity.email_message.from,
                to: activity.email_message.to,
                replyTo: activity.email_message.reply_to,
                cc: activity.email_message.cc,
                bcc: activity.email_message.bcc,
                date: activity.email_message.date,
                isSent: activity.email_message.is_sent,
              }}
              onClose={onClose}
            />
          ) : (
            <EmailFormComponent
              mode="forward"
              forwardMessage={{
                messageId: activity.email_message.message_id,
                createdBy: activity.email_message.created_by,
                subject: activity.email_message.subject,
                from: activity.email_message.from,
                date: activity.email_message.date,
              }}
              onClose={onClose}
            />
          )}
        </TimelineContent>
      </TimelineItem>
    </Collapse>
  );
};
